import { IconMenu2 } from '@tabler/icons-react';
import {
  ActionIcon,
  Box,
  Button,
  Text,
  Group,
  Flex,
  AppShellHeader,
  Menu,
  Image,
  SegmentedControl,
} from '@mantine/core';
import { useMemo, useState, useEffect } from 'react';
import { useMediaQuery } from '@mantine/hooks';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { useLeague } from '../../context/LeagueContext';
import { LEAGUE } from '../../models/NBA';
import { NBA_COLOR, WNBA_COLOR } from '../../theme';
import { isLeagueSwitcherVisible } from '../../utils/league';
import { urlSafeBase64Decode } from '../../utils/utils';

export const NBAInsightsHeader: React.FC = () => {
  const { user, isAuthenticated, loginWithRedirect, logout } = useAuth0();
  const { league, setLeague, getSearchUrl } = useLeague();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const isMobile = useMediaQuery('(max-width: 768px)');

  const videoLeague = useMemo(() => {
    if (!pathname.startsWith('/video/')) return null;
    try {
      const hash = pathname.slice('/video/'.length);
      const [gameId] = urlSafeBase64Decode(hash).split(':');
      if (gameId?.startsWith('10')) return LEAGUE.WNBA;
      if (gameId?.startsWith('00')) return LEAGUE.NBA;
      return null;
    } catch {
      return null;
    }
  }, [pathname]);

  const activeLeague = videoLeague || league;
  const [optimisticLeague, setOptimisticLeague] = useState<LEAGUE>(activeLeague);

  useEffect(() => {
    setOptimisticLeague(activeLeague);
  }, [activeLeague]);

  return (
    <AppShellHeader style={{ alignContent: "center" }} px={{ base: 12, sm: 20 }}>
      <Flex justify="space-between" align="center" w="100%" h="100%" style={{ position: 'relative' }}>

        {/* Far Left: Brand Logo & Title */}
        <Flex align="center">
          <Group
            gap="xs"
            style={{ cursor: 'pointer', userSelect: 'none' }}
            onClick={() => navigate('/')}
          >
            <Image
              src="/favicon.ico"
              alt="NBA Insights"
              w={30}
              h={30}
              radius="xl"
              hiddenFrom="sm"
            />
            <Text
              c="white"
              fz="26px"
              fw={800}
              visibleFrom="sm"
              style={{ whiteSpace: 'nowrap', fontFamily: 'var(--mantine-font-family-headings)', letterSpacing: '0.5px' }}
            >
              {optimisticLeague === LEAGUE.WNBA ? 'WNBA Insights' : 'NBA Insights'}
            </Text>
          </Group>
        </Flex>

        {/* Center: League Switcher (True Absolute Centering) */}
        {isLeagueSwitcherVisible(pathname) && (
          <Box
            style={{
              position: 'absolute',
              left: '50%',
              transform: 'translateX(-50%)',
              pointerEvents: 'auto',
              zIndex: 1,
            }}
          >
            <SegmentedControl
              size={isMobile ? "xs" : "md"}
              radius="xl"
              value={optimisticLeague}
              onChange={(val) => {
                const newLeague = val as LEAGUE;
                setOptimisticLeague(newLeague);
                setLeague(newLeague);
                window.scrollTo(0, 0);
                document.documentElement.scrollTop = 0;
                document.body.scrollTop = 0;
                if (pathname === '/search' || pathname.startsWith('/video')) {
                  navigate(getSearchUrl(newLeague));
                }
              }}
              data={[
                { label: 'NBA', value: LEAGUE.NBA },
                { label: 'WNBA', value: LEAGUE.WNBA },
              ]}
              color={optimisticLeague === LEAGUE.WNBA ? WNBA_COLOR : NBA_COLOR}
              styles={{
                root: {
                  backgroundColor: '#202020',
                  border: '1px solid #383838',
                  padding: isMobile ? 2 : 4,
                },
                label: {
                  fontWeight: 700,
                  fontSize: isMobile ? 12 : 14,
                  padding: isMobile ? '3px 8px' : '5px 18px',
                },
                indicator: {
                  boxShadow: `0 0 12px ${optimisticLeague === LEAGUE.WNBA ? 'rgba(255, 85, 0, 0.5)' : 'rgba(29, 155, 240, 0.5)'}`,
                },
              }}
            />
          </Box>
        )}

        {/* Far Right: Navigation & User Auth */}
        <Flex justify="flex-end" align="center" gap="xs">
          <Group gap={6} visibleFrom="sm">
            <Button
              fz="md"
              fw={600}
              c={pathname === "/search" ? "accent" : "white"}
              variant="transparent"
              onClick={() => navigate(getSearchUrl(league))}
            >
              Search
            </Button>
            <Button
              fz="md"
              fw={600}
              c={pathname === "/playlists" ? "accent" : "white"}
              variant="transparent"
              onClick={() => navigate("/playlists")}
            >
              Playlists
            </Button>
            { !isAuthenticated && (
              <Button
                variant="filled"
                size="sm"
                radius="xl"
                ml="xs"
                px="lg"
                fz="md"
                fw={600}
                onClick={() => loginWithRedirect()}
              >
                Login
              </Button>
            )}
            { isAuthenticated && (
              <Group gap="xs" ml="xs">
                <Button
                  variant="subtle"
                  size="sm"
                  fz="md"
                  onClick={() => logout({ logoutParams: { returnTo: window.location.origin } })}
                >
                  Logout
                </Button>
                <Image alt="profile" radius="xl" src={user?.picture} w={36} h={36} />
              </Group>
            )}
          </Group>

          {/* Mobile Hamburger Menu */}
          <Group hiddenFrom="sm">
            <Menu trigger="click" width={180}>
              <Menu.Target>
                <ActionIcon variant="transparent" c="white" size="lg">
                  <IconMenu2 size={22} />
                </ActionIcon>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Item onClick={() => navigate("/")}>
                  <Menu.Label c="white" fz="md">Home</Menu.Label>
                </Menu.Item>
                <Menu.Item onClick={() => navigate(getSearchUrl(league))}>
                  <Menu.Label c="white" fz="md">Search</Menu.Label>
                </Menu.Item>
                <Menu.Item onClick={() => navigate("/playlists")}>
                  <Menu.Label c="white" fz="md">Playlists</Menu.Label>
                </Menu.Item>
                { isAuthenticated ? (
                  <Menu.Item onClick={() => logout({ logoutParams: { returnTo: window.location.origin } })}>
                    <Menu.Label c="white" fz="md">Logout</Menu.Label>
                  </Menu.Item>
                ) : (
                  <Menu.Item onClick={() => loginWithRedirect()}>
                    <Menu.Label c="white" fz="md">Login</Menu.Label>
                  </Menu.Item>
                )}
              </Menu.Dropdown>
            </Menu>
          </Group>
        </Flex>

      </Flex>
    </AppShellHeader>
  );
}